import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchIssues } from "../lib/api";
import { useIssueStore } from "../lib/issue-store";
import { useNotify } from "../lib/notify-store";
import { roleCan, useRole } from "../lib/roles";
import { IssueWorkspace } from "../components/IssueWorkspace";
import { PageSkeleton } from "../components/PageSkeleton";
import { PageHeader, PrimaryButton } from "../components/primitives";

export function ContentPage() {
  const { role } = useRole();
  const notify = useNotify();
  const navigate = useNavigate();
  const seed = useQuery({ queryKey: ["issues"], queryFn: fetchIssues });
  const store = useIssueStore(seed.isError ? [] : seed.data);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const freelancer = role === "freelancer";

  const issues = store.issues ?? [];

  // Nothing is chosen until someone clicks a newsletter: an auto-selected one
  // kept a detail panel open that squeezed the board into a third of the page.


  if (!store.ready && seed.isLoading) return <PageSkeleton />;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <PageHeader
        title={freelancer ? "My Newsletters" : "Newsletters"}
        meta={freelancer
          ? "Write, preview, then submit. Editors handle audience and sending."
          : "Write and design newsletters, send them for review, approve them. Approved ones are sent from Broadcasts."}
        actions={
          <PrimaryButton
            disabled={!roleCan(role, "newsletters.write")}
            onClick={() => {
              const draft = store.create({ title: "", subject: "" });
              navigate(`/content/${draft.id}`, { state: { issue: draft } });
            }}
          >
            {freelancer ? "+ Create Newsletter" : "New newsletter"}
          </PrimaryButton>
        }
      />
      <IssueWorkspace
        issues={issues}
        selectedId={selectedId}
        onSelect={setSelectedId}
        canApprove={roleCan(role, "newsletters.approve")}
        compact={freelancer}
        actions={{
          onUpdate: store.updateFields,
          onDetach: store.detach,
          onSubmit: (id) => {
            const issue = issues.find((i) => i.id === id);
            store.submitForReview(id);
            if (issue) {
              notify.push({
                kind: "submitted",
                title: "Submitted for review",
                body: `“${issue.title}” is waiting for an editor.`,
                href: "/content",
                forRoles: ["editor", "admin"],
              });
            }
          },
          onApprove: (id) => {
            const issue = issues.find((i) => i.id === id);
            store.approve(id);
            if (issue) {
              notify.push({
                kind: "approved",
                title: "Newsletter approved",
                body: `“${issue.title}” is approved and ready to send.`,
                href: "/broadcasts",
                forRoles: ["freelancer", "editor", "admin"],
              });
            }
          },
          onRequestChanges: (id, comment, blockId) => {
            const issue = issues.find((i) => i.id === id);
            store.requestChanges(id, comment, blockId);
            notify.push({
              kind: "changes",
              title: issue ? `Changes requested on “${issue.title}”` : "Changes requested",
              body: comment.trim() || "Please revise this draft and resubmit.",
              href: issue ? `/content/${issue.id}` : "/content",
              forRoles: ["freelancer", "admin"],
            });
          },
        }}
      />
    </div>
  );
}
